import { useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, X, FileText } from "lucide-react";

// Iter 67 — In-app attachment previewer.
// Decides the viewer by MIME type:
//   • PDF → <iframe> using ?inline=1 endpoint (browser's native PDF viewer)
//   • Images → <img>
//   • text/plain or *.csv/.txt → <iframe> (browser renders as text)
//   • Everything else → metadata + Download / Open-in-new-tab buttons
//
// Renders inside a Shadcn Dialog so it stacks above the inbox cleanly.
// `open` is controlled by the parent (Triage); `onClose` clears it.
//
// Props:
//   - attachment: { filename, mime_type, attachment_id, size }
//   - gmail_id:   the parent message's Gmail ID (required for the URL)

export default function AttachmentPreview({ open, attachment, gmailId, onClose }) {
  // Esc closes (Dialog handles this for us, but we also want to clear
  // any scroll lock applied by the inbox below).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!attachment || !gmailId) return null;
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/gmail/attachment/${gmailId}/${attachment.attachment_id}`;
  const inlineUrl = `${base}?inline=1`;
  const downloadUrl = base;
  const mime = (attachment.mime_type || "").toLowerCase();
  const isPdf = mime === "application/pdf" || (attachment.filename || "").toLowerCase().endsWith(".pdf");
  const isImage = mime.startsWith("image/");
  const isText = mime.startsWith("text/") || /\.(txt|csv|md|json|log)$/i.test(attachment.filename || "");
  const sizeKb = attachment.size ? Math.round(attachment.size / 1024) : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        className="sm:max-w-5xl md:max-w-6xl p-0 gap-0 h-[90vh] flex flex-col"
        data-testid="attachment-preview-dialog"
      >
        <DialogTitle className="sr-only">{attachment.filename}</DialogTitle>
        <div className="bg-slate-100 border-b border-slate-200 px-4 py-2 flex items-center gap-3 shrink-0">
          <FileText className="h-4 w-4 text-slate-500 shrink-0"/>
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate">{attachment.filename}</div>
            <div className="text-[11px] text-slate-500 tabular-nums">
              {sizeKb ? `${sizeKb} KB` : "—"} · {attachment.mime_type || "unknown"}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-1">
            <Button
              size="sm" variant="ghost"
              data-testid="attachment-preview-open-tab"
              onClick={() => window.open(inlineUrl, "_blank", "noopener,noreferrer")}
              className="text-slate-600 hover:text-slate-900"
              title="Open in new tab"
            >
              <ExternalLink className="h-4 w-4"/>
            </Button>
            <a
              href={downloadUrl}
              download={attachment.filename}
              data-testid="attachment-preview-download"
              className="inline-flex items-center text-slate-600 hover:text-slate-900 px-2 py-1.5 rounded hover:bg-slate-200 text-sm"
              title="Download"
            >
              <Download className="h-4 w-4"/>
            </a>
            <Button
              size="sm" variant="ghost"
              data-testid="attachment-preview-close"
              onClick={onClose}
              className="text-slate-600 hover:text-slate-900"
              title="Close"
            >
              <X className="h-4 w-4"/>
            </Button>
          </div>
        </div>
        <div className="flex-1 min-h-0 bg-slate-900/5">
          {isPdf || isText ? (
            <iframe
              src={inlineUrl}
              title={attachment.filename}
              className="w-full h-full border-0"
              data-testid="attachment-preview-iframe"
            />
          ) : isImage ? (
            <div className="w-full h-full flex items-center justify-center overflow-auto p-4">
              <img
                src={inlineUrl}
                alt={attachment.filename}
                className="max-w-full max-h-full object-contain"
                data-testid="attachment-preview-image"
              />
            </div>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-center px-6 gap-3" data-testid="attachment-preview-unsupported">
              <FileText className="h-12 w-12 text-slate-300"/>
              <div className="text-sm text-slate-700 font-medium">No inline preview available</div>
              <div className="text-xs text-slate-500 max-w-md">
                {attachment.mime_type || "This file type"} files can&apos;t be previewed inside Close. Download to open with your default application.
              </div>
              <a
                href={downloadUrl}
                download={attachment.filename}
                className="mt-2 inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium px-4 py-2 rounded-md"
              >
                <Download className="h-3.5 w-3.5"/> Download {attachment.filename}
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
